import React, { useState } from "react";

import { faExclamationCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tippy, { useSingleton } from "@tippyjs/react";
import cx from "classnames";
import { EditorView } from "prosemirror-view";
import { roundArrow } from "tippy.js";
import "tippy.js/dist/svg-arrow.css";
import "tippy.js/dist/tippy.css";

import { Button } from "~/ui";
import { HelpCenterIcon, IconButton, Input, Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toast from "~/ui/toast";

import {
	createEmbedNode,
	detectService,
	editEmbedNode,
	type EmbedObject,
	type ExistingEmbed,
	getEmbedObject,
	getUrl,
} from "./helpers";
import supportedServices from "./supportedServices";

import styles from "./EmbedModal.module.css";

const INPUT_EMPTY = "Please enter a URL.";
export const INVALID_URL = "Please enter a valid URL.";
const SERVICE_NOT_SUPPORTED = "We don't support that link, yet.";

interface EmbedModalProps {
	close: () => void;
	view: EditorView;
	existingEmbed?: ExistingEmbed;
}

export default function EmbedModal({ existingEmbed, view, close }: EmbedModalProps) {
	const [urlInput, setUrlInput] = useState("");
	const existingDetails = existingEmbed ? getEmbedObject(existingEmbed.service) : undefined;
	const [embedDetails, setEmbedDetails] = useState<EmbedObject | undefined>(existingDetails);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const placeholder = embedDetails
		? `e.g. https://${embedDetails.domains[0]}/...`
		: "e.g. https://www.youtube.com/watch?v=..."; //arbitrary choice as an example URL for the generic modal

	const isInputError = (input: string): boolean => {
		if (!input) {
			setErrorMessage(INPUT_EMPTY);
			return true;
		} else if (!getUrl(input)) {
			setErrorMessage(INVALID_URL);
			return true;
		} else if (!detectService(input)) {
			setErrorMessage(SERVICE_NOT_SUPPORTED);
			return true;
		} else {
			setErrorMessage(null);
			return false;
		}
	};

	const onUrlInput = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const input = event.target.value;
			setUrlInput(input);

			if (isInputError(input)) return setEmbedDetails(existingDetails);

			const service = detectService(input);
			if (!service) return setEmbedDetails(existingDetails);

			if (existingEmbed) {
				if (service !== existingEmbed.service) {
					setErrorMessage(`Please enter a ${existingDetails?.name} link.`);
				}
				return;
			}

			const details = getEmbedObject(service);

			setEmbedDetails(details);
		},
		[existingDetails, existingEmbed],
	);

	const onClickOkay = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (isInputError(urlInput) || !embedDetails) return;
		setLoading(true);

		try {
			const url = getUrl(urlInput);
			if (!url) return;

			const parseResult = await embedDetails.getEmbedNodeAttributes(url);
			if (parseResult instanceof Error) {
				setErrorMessage(parseResult.message);
				setLoading(false);
				return;
			}

			if (existingEmbed) {
				editEmbedNode(view, parseResult, existingEmbed.nodeToReplace);
			} else {
				createEmbedNode(view, parseResult);
			}
		} catch (error) {
			toast.error("Sorry, something went wrong.");
			console.error(error);
		}

		setLoading(false);
		close();
	};

	const [source, target] = useSingleton();

	return (
		<Modal onClose={close} width="30rem">
			<Modal.Header>
				<Modal.Title>{existingDetails ? `Edit ${existingDetails.name} embed` : "Embed media"}</Modal.Title>
				<Modal.HeaderActions onClose={close}>
					<HelpCenterIcon articleId="5416827" title="Embedding media" />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.body}>
				{errorMessage && (
					<div className={styles.errorBanner}>
						<FontAwesomeIcon className={styles.bannerIcon} icon={faExclamationCircle} />
						{errorMessage}
						<div className={styles.close} onClick={() => setErrorMessage(null)}>
							<IconButton className={styles.closeButton} icon={faTimes} />
						</div>
					</div>
				)}
				<div className={styles.row}>
					{existingDetails
						? `Paste the ${existingDetails.name} link which will replace this embed.`
						: "Paste a link to embed in the note."}
				</div>
				<form onSubmit={onClickOkay} className={styles.row}>
					<Input
						name="embed_url"
						autoFocus
						type="text"
						value={urlInput}
						onChange={onUrlInput}
						placeholder={placeholder}
						className={styles.embedInput}
					/>
					<Button size="normal" variant="primary" type="submit" className={styles.embedButton}>
						{loading ? <LoadingSpinner inline size="xs" className={styles.loader} /> : "Embed"}
						{/* Note: changed size from "tiny" to "xs" as FontAwesome sizes differ from semantic-ui */}
					</Button>
				</form>
				{!existingDetails && (
					<div className={styles.supportedServices}>
						<p>Supported services:</p>
						<Tippy singleton={source} placement="bottom" arrow={roundArrow} maxWidth={220} />

						{Object.entries(supportedServices).map(([key, embedService]) => {
							const isSelected = embedService.name === embedDetails?.name;
							const serviceIcon = embedService.icon;

							return (
								<Tippy key={key} singleton={target} content={embedService.name}>
									<div
										className={cx(styles.embedIcon, {
											[styles.selected]: isSelected,
										})}
									>
										{serviceIcon}
									</div>
								</Tippy>
							);
						})}
					</div>
				)}
			</Modal.Body>
		</Modal>
	);
}
